import { CHAR_W, LINE_H, fitBallText } from './hopperSim'
import type { Pt } from './hopperSim'

/* ---------------------------------------------------------------------------
 * The SVG flight layer: the tube, the gutter, the ports and the ball in the
 * air, ported from the `dir-lottery` mockup with one structural change.
 *
 * The mockup pinned `viewBox="0 0 1280 720"` and mapped DOM boxes into it with
 * `k = 1280 / rect.width`. Those two agree only when the stage is 16:9; on a
 * 390x844 phone `preserveAspectRatio` paints user space into a letterboxed
 * 390x219 band while `measure()` keeps reporting socket centres near y = 2767,
 * so every port lands off-canvas and the tube points into empty space.
 *
 * Measuring the viewBox off the stage's own box makes k exactly 1 — one SVG
 * user unit is one CSS pixel — and a DOM box then maps in by subtraction alone,
 * at any aspect ratio. Everything the mockup left as a fixed user-space number
 * (stroke widths, radii, the elbow radius) instead gets one scale u = W/1280,
 * which is precisely what `preserveAspectRatio` was doing to them before, so
 * the landscape render is byte-identical rather than merely similar.
 *
 * This module is a *renderer*, not a sequencer: no timers, no rAF, no
 * callbacks, and no duration of its own. One `render(frame)` paints one
 * instant, which is what makes a seek — forwards or backwards — identical to a
 * plain frame.
 * ------------------------------------------------------------------------ */

// 'gutter', not 'land', so this is structurally assignable from #77's
// LotteryPhase — lib/lotteryTimeline.ts names the climb after the rail it runs
// up rather than after the instant it ends on, and that module is the one the
// page hands straight to render(). The beat's duration field stays landMs:
// that one is an instant, not a segment.
export type FlightSegment = 'funnel' | 'tube' | 'gutter'

export interface FlightFrame {
  /** Picks already in their slots, any order. The gutter fill and the port
   *  lamps derive from this and nothing else — that is the whole seek story. */
  landed: number[]
  /** The ball in the air, or null between beats. `from` and `label` come from
   *  the hopper; this module never touches the sim. */
  flight: {
    pick: number
    segment: FlightSegment
    /** 0..1 within the segment. */
    t: number
    from: Pt
    label: string
  } | null
}

export interface FlightLayerRefs {
  stage: HTMLElement
  svg: SVGSVGElement
  funnel: HTMLElement
  rail: HTMLElement
  /** Index = pick - 1. DOM order is pick 1..N; the rail stacks bottom-up with
   *  `justify-content: flex-end`, so this is *not* visual order. */
  sockets: HTMLElement[]
}

/* dir-lottery.js's user space. Every number below is in it, and gets one
 * multiply by u. Keeping the reference values means the landscape render is
 * identical to the mockup at 1280x720 rather than merely similar. */
/* Slimmer than the mockup's 46/36/30. The tube ends in a round cap at the
 * nozzle, so the drawn geometry reaches half a wall *beyond* the path in every
 * direction — at 46 that cap climbed into the bottom row's socket and sat under
 * the last ball. Thinning the wall and moving the gutter left (below) buys the
 * clearance between them; the bore keeps its ~78% share so the pipe still reads
 * as a pipe rather than as a line. */
const TUBE = { wall: 40, bore: 31, lite: 3, dash: 1.5, flow: 26 }
const FLY = { body: 26, glow: 34, ring: 1.5 }
const GHOST_R = [20, 15, 10]
const PORT_R = 3.4
const CORNER = 78 // the mockup's first elbow radius (:648, 682 - 604)
/* Gutter x, left of the rail's left edge. The mockup's 6 put the whole
 * tube-and-gutter assembly directly under the first column of the rail; it has
 * to clear half a wall plus the row's own left padding, or the nozzle's cap
 * overlaps the bottom socket. */
const GUT_INSET = 20
const GUT_STROKE = 2 // the gutter rail's own stroke width
const NOZZLE_INSET = 2 // nozzle x, left of the gutter
/* The tube's floor run, below the nozzle. The mockup's 24 put the run's top
 * edge within a unit of the nozzle — with a 46-unit wall the pipe wrapped the
 * bottom row hard enough to read as a collision rather than as plumbing. 56
 * clears it, and it lengthens the vertical leg, which is the input the second
 * elbow's radius is clamped against: a longer leg means a rounder bend. */
const FLOOR_DROP = 64
/* Kept clear beneath the tube's outer edge, as a backstop. The run is normally
 * centred in the space under the rail and never gets near this. */
const FLOOR_MARGIN = 16
const GUT_TOP_LIFT = 14 // gutter top, above the pick-1 socket centre
const GUT_BASE_DROP = 2 // gutter base, below the nozzle

/* The nozzle plate and its lamp, as offsets from the nozzle point. The mockup
 * hardcoded them at (493,652,22x19) and (504,661) against a fixed nozzle of
 * (504,658); with the nozzle now measured they have to travel with it, or the
 * plate sits in the middle of the stage at anything but 1280x720. */
const NOZZLE_BOX = { dx: -11, dy: -6, w: 22, h: 19, r: 3 }
const LAMP = { dy: 3, r: 3.2 }

/* The amber pulse that runs down the bore behind a ball, in user space. The
 * dash scales with u; the `@keyframes lottery-flow` dashoffset does not, so the
 * pulse travels proportionally less far on a large stage. It is a 460ms
 * flourish behind a ball that has already gone, and giving it a second scale to
 * reason about is not worth the correctness it would buy. */
const FLOW_DASH = 26
const FLOW_GAP = 999

/* .lot-sock is 2.188cqw of the stage, so 2.188% x 1280 = 28.0064, and
 * sockD / SOCK_REF is *identically* W / 1280 in landscape — the same number,
 * not an approximation of it.
 *
 * Deriving the one scale from the socket rather than from the stage width is
 * what makes portrait work: there, the socket is sized off the rail's row
 * height, so the machine's whole scale follows the thing the tube has to
 * deliver a ball into — the only relationship in this composition that has to
 * hold. Off stage width instead, a 390px stage puts the flying ball at r = 7.9
 * against a socket the portrait layout has grown to 20.7. */
const SOCK_REF = 28.0064

/** The one scale between the mockup's user space and measured pixels. Exactly
 *  1 at the reference socket, which is what makes the landscape render a
 *  byte-for-byte port. */
export function unitScale(socketWidth: number): number {
  return socketWidth / SOCK_REF
}

/* Cubic control offset from a corner vertex, as a fraction of the radius.
 * 1 - 0.5523 is the circular-arc value; the mockup's hand-authored elbows sat
 * at ~0.39r, which is not visually distinguishable at these radii. */
const CORNER_K = 0.4477

/* One decimal is ~0.1 CSS px. Fixed here rather than left to the caller so the
 * emitted string is deterministic and can be asserted. */
const round = (n: number) => Math.round(n * 10) / 10

const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y)

function unit(from: Pt, to: Pt): Pt {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const d = Math.hypot(dx, dy)
  return { x: dx / d, y: dy / d }
}

/**
 * A rounded polyline through `pts`. Replaces the six hand-written copies of a
 * literal `d` at dir-lottery.js:648-653, which pinned the tube to a 1280x720
 * user space and are the single reason portrait could not be a CSS variant of
 * landscape.
 */
export function pathFor(pts: Pt[], r: number): string {
  if (pts.length < 2) return ''
  let d = `M${round(pts[0].x)} ${round(pts[0].y)}`
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1]
    const cur = pts[i]
    const next = pts[i + 1]
    // Clamped to half the shorter adjoining leg so a short final leg — the
    // 24-unit hop up to the nozzle — degrades instead of overshooting past the
    // next vertex. Zero-length legs make `unit` NaN, so they are skipped: a
    // corner with nowhere to turn is just a point on the line.
    const ri = Math.min(r, dist(cur, prev) / 2, dist(cur, next) / 2)
    if (!(ri > 0)) continue
    const a = unit(cur, prev)
    const b = unit(cur, next)
    d += ` L${round(cur.x + a.x * ri)} ${round(cur.y + a.y * ri)}`
    d += ` C${round(cur.x + a.x * ri * CORNER_K)} ${round(cur.y + a.y * ri * CORNER_K)}`
    d += ` ${round(cur.x + b.x * ri * CORNER_K)} ${round(cur.y + b.y * ri * CORNER_K)}`
    d += ` ${round(cur.x + b.x * ri)} ${round(cur.y + b.y * ri)}`
  }
  const last = pts[pts.length - 1]
  return `${d} L${round(last.x)} ${round(last.y)}`
}

function bez2(p0: Pt, p1: Pt, p2: Pt, t: number): Pt {
  const u = 1 - t
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  }
}

function bez3(p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): Pt {
  const u = 1 - t
  const uu = u * u
  const tt = t * t
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  }
}

export class FlightLayer {
  private readonly stage: HTMLElement
  private readonly svg: SVGSVGElement
  private readonly funnel: HTMLElement
  private readonly rail: HTMLElement
  private readonly sockets: HTMLElement[]

  private readonly tubes: SVGPathElement[]
  private readonly path: SVGPathElement | null
  private readonly flow: SVGPathElement | null
  private readonly gut: SVGLineElement | null
  private readonly gutFill: SVGRectElement | null
  private readonly ports: SVGCircleElement[]
  private readonly nozzlePlate: SVGRectElement | null
  private readonly lamp: SVGCircleElement | null
  private readonly flyer: SVGGElement | null
  private readonly flyGlow: SVGCircleElement | null
  private readonly flyBody: SVGCircleElement | null
  private readonly flyRing: SVGCircleElement | null
  private readonly flyTxt: SVGTextElement | null
  private readonly ghosts: SVGCircleElement[]

  private measured = false
  private u = 1
  private sock: Pt[] = []
  private mouth: Pt = { x: 0, y: 0 }
  private nozzle: Pt = { x: 0, y: 0 }
  private gutX = 0
  private tubeLen = 0

  /* A short position ring for the three ghost circles, cleared whenever the
   * flight goes null or changes pick. A scrubbed-to instant has no past, so a
   * seek correctly starts with no trail and rebuilds one within three frames. */
  private trail: Pt[] = []
  private trailPick: number | null = null
  /** Last label laid out, so the tspans are rebuilt per ball, not per frame. */
  private flyLabel = ''

  constructor(refs: FlightLayerRefs) {
    this.stage = refs.stage
    this.svg = refs.svg
    this.funnel = refs.funnel
    this.rail = refs.rail
    this.sockets = refs.sockets

    const q = <T extends Element>(sel: string) => this.svg.querySelector<T>(sel)
    const qq = <T extends Element>(sel: string) => Array.from(this.svg.querySelectorAll<T>(sel))

    this.tubes = qq<SVGPathElement>(
      '.lot-tube-wall, .lot-tube-bore, .lot-tube-lite, .lot-tube-dash, .lot-tube-flow, .lot-path',
    )
    this.path = q<SVGPathElement>('.lot-path')
    this.flow = q<SVGPathElement>('.lot-tube-flow')
    this.gut = q<SVGLineElement>('.lot-gut')
    this.gutFill = q<SVGRectElement>('.lot-gutfill')
    this.ports = qq<SVGCircleElement>('.lot-port')
    this.nozzlePlate = q<SVGRectElement>('.lot-nozzle')
    this.lamp = q<SVGCircleElement>('.lot-nozzle-lamp')
    this.flyer = q<SVGGElement>('.lot-flyer')
    this.flyGlow = q<SVGCircleElement>('.lot-fly-glow')
    this.flyBody = q<SVGCircleElement>('.lot-fly-body')
    this.flyRing = q<SVGCircleElement>('.lot-fly-ring')
    this.flyTxt = q<SVGTextElement>('.lot-fly-txt')
    this.ghosts = qq<SVGCircleElement>('.lot-ghost')
  }

  /** Re-reads every DOM box and rewrites the viewBox, the tube `d`, the stroke
   *  widths, the radii, the gutter and the ports. False when the stage has no
   *  size yet (first paint, and always in jsdom). */
  measure(): boolean {
    const box = this.stage.getBoundingClientRect()
    if (!box.width || !box.height) return false
    const sockW = this.sockets[0]?.getBoundingClientRect().width ?? 0
    if (!sockW) return false
    const u = unitScale(sockW)
    this.u = u

    // k = 1. dir-lottery.js:722 needed k = 1280 / rect.width because its user
    // space was fixed; that mismatch is the whole reason the mockup letterboxes
    // on a phone. With the viewBox measured, a DOM box maps in by subtraction.
    this.svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`)

    this.sock = this.sockets.map((el) => {
      const b = el.getBoundingClientRect()
      return { x: b.left + b.width / 2 - box.left, y: b.top + b.height / 2 - box.top }
    })

    const fb = this.funnel.getBoundingClientRect()
    this.mouth = { x: fb.left + fb.width / 2 - box.left, y: fb.bottom - box.top }

    const railLeft = this.rail.getBoundingClientRect().left - box.left
    this.gutX = railLeft - GUT_INSET * u
    const last = this.sock[this.sock.length - 1]
    this.nozzle = { x: this.gutX - NOZZLE_INSET * u, y: last ? last.y : this.mouth.y }

    // Centred in whatever space is left under the rail, rather than dropped a
    // fixed distance. A fixed drop has to be tuned per aspect and gets it wrong
    // both ways: too shallow and the run wraps the bottom row, too deep and it
    // is pressed against the floor of the stage. Splitting the leftover gives
    // the same gap above and below on any stage.
    //
    // Still bounded. FLOOR_DROP caps it so a very tall stage does not strand the
    // run halfway down the screen, and FLOOR_MARGIN keeps the outer wall clear
    // of the floor — overflow:hidden on the stage would simply cut it off.
    const halfWall = (TUBE.wall / 2) * u
    const lastSock = this.sockets[this.sockets.length - 1]?.getBoundingClientRect()
    const sockBottom = lastSock ? lastSock.bottom - box.top : this.nozzle.y
    const floorY = Math.max(
      this.nozzle.y + GUT_BASE_DROP * u,
      Math.min(
        (sockBottom + box.height) / 2,
        this.nozzle.y + FLOOR_DROP * u,
        box.height - halfWall - FLOOR_MARGIN * u,
      ),
    )
    const d = pathFor(
      [this.mouth, { x: this.mouth.x, y: floorY }, { x: this.nozzle.x, y: floorY }, this.nozzle],
      CORNER * u,
    )
    // The mockup's `.lot-tube-lite` and `.lot-tube-dash` carried their own
    // hand-nudged `d`, offset a few units inside the bend. Those offset copies
    // do not survive generation, and at these stroke widths a highlight down
    // the bore's centreline is indistinguishable from one beside it.
    for (const p of this.tubes) p.setAttribute('d', d)
    this.setStroke('.lot-tube-wall', TUBE.wall * u)
    this.setStroke('.lot-tube-bore', TUBE.bore * u)
    this.setStroke('.lot-tube-lite', TUBE.lite * u)
    this.setStroke('.lot-tube-dash', TUBE.dash * u)
    this.setStroke('.lot-tube-flow', TUBE.flow * u)
    this.flow?.setAttribute('stroke-dasharray', `${round(FLOW_DASH * u)} ${round(FLOW_GAP * u)}`)

    this.ports.forEach((port, i) => {
      const s = this.sock[i]
      if (!s) return
      port.setAttribute('cx', String(round(this.gutX)))
      port.setAttribute('cy', String(round(s.y)))
      port.setAttribute('r', String(round(PORT_R * u)))
    })

    if (this.sock.length && this.gut && this.gutFill) {
      this.gut.setAttribute('x1', String(round(this.gutX)))
      this.gut.setAttribute('x2', String(round(this.gutX)))
      this.gut.setAttribute('y1', String(round(this.sock[0].y - GUT_TOP_LIFT * u)))
      this.gut.setAttribute('y2', String(round(this.nozzle.y + GUT_BASE_DROP * u)))
      this.gut.setAttribute('stroke-width', String(round(GUT_STROKE * u)))
      this.gutFill.setAttribute('x', String(round(this.gutX - u)))
      this.gutFill.setAttribute('width', String(round(2 * u)))
      this.gutFill.setAttribute('rx', String(round(u)))
    }

    if (this.nozzlePlate) {
      this.nozzlePlate.setAttribute('x', String(round(this.nozzle.x + NOZZLE_BOX.dx * u)))
      this.nozzlePlate.setAttribute('y', String(round(this.nozzle.y + NOZZLE_BOX.dy * u)))
      this.nozzlePlate.setAttribute('width', String(round(NOZZLE_BOX.w * u)))
      this.nozzlePlate.setAttribute('height', String(round(NOZZLE_BOX.h * u)))
      this.nozzlePlate.setAttribute('rx', String(round(NOZZLE_BOX.r * u)))
      this.nozzlePlate.setAttribute('stroke-width', String(round(u)))
    }
    if (this.lamp) {
      this.lamp.setAttribute('cx', String(round(this.nozzle.x)))
      this.lamp.setAttribute('cy', String(round(this.nozzle.y + LAMP.dy * u)))
      this.lamp.setAttribute('r', String(round(LAMP.r * u)))
    }

    this.flyGlow?.setAttribute('r', String(round(FLY.glow * u)))
    this.flyBody?.setAttribute('r', String(round(FLY.body * u)))
    this.flyRing?.setAttribute('r', String(round(FLY.body * u)))
    this.flyRing?.setAttribute('stroke-width', String(round(FLY.ring * u)))
    // font-size and the per-line y are set by setFlyLabel; a re-measure has to
    // force it to run again because the radius it sizes against has changed.
    this.flyLabel = ''
    this.ghosts.forEach((g, i) => g.setAttribute('r', String(round((GHOST_R[i] ?? 0) * u))))

    // jsdom implements neither getTotalLength nor getPointAtLength. The zero
    // fallback is the only reason this class can be rendered in a test at all —
    // render()'s tube segment lerps mouth -> nozzle when the length is 0.
    this.tubeLen =
      this.path && typeof this.path.getTotalLength === 'function' ? this.path.getTotalLength() : 0

    // Three ghosts left at pre-rotation coordinates are the one artefact a
    // viewer actually notices, and a scrubbed-to instant having no trail is
    // already the established behaviour after a seek.
    this.trail = []
    this.trailPick = null
    for (const g of this.ghosts) g.setAttribute('opacity', '0')

    this.measured = true
    return true
  }

  render(frame: FlightFrame): void {
    if (!this.measured && !this.measure()) return
    const u = this.u
    const landed = new Set(frame.landed)
    this.ports.forEach((port, i) => port.classList.toggle('is-hit', landed.has(i + 1)))

    // The gutter fill is a function of the *lowest-numbered* landed pick, not
    // of a running total. The draw goes 12 -> 1, so the fill height is
    // monotonic in draw order, and recomputing it from scratch every frame is
    // what lets a seek — forwards or backwards — be a plain re-render.
    let fillY = this.nozzle.y + GUT_BASE_DROP * u
    if (frame.landed.length) {
      const top = this.sock[Math.min(...frame.landed) - 1]
      if (top) fillY = top.y
    }

    const flight = frame.flight
    if (!flight || flight.pick !== this.trailPick) this.trail = []
    this.trailPick = flight ? flight.pick : null

    if (!flight) {
      this.funnel.classList.remove('is-open')
      this.lamp?.classList.remove('is-hot')
      this.flow?.classList.remove('is-on')
      this.flyer?.setAttribute('opacity', '0')
      for (const g of this.ghosts) g.setAttribute('opacity', '0')
      this.fillGutterTo(fillY)
      return
    }

    const t = Math.min(1, Math.max(0, flight.t))
    let pt: Pt
    let sx = 1
    let sy = 1

    if (flight.segment === 'funnel') {
      const ctrlA = { x: flight.from.x * 0.32 + this.mouth.x * 0.68, y: flight.from.y + 26 * u }
      pt = bez2(flight.from, ctrlA, this.mouth, t * t)
      sx = 1 - 0.1 * t
      sy = 1 + 0.12 * t
    } else if (flight.segment === 'tube') {
      const sm = t * t * (3 - 2 * t)
      if (this.tubeLen && this.path) {
        const q = this.path.getPointAtLength(sm * this.tubeLen)
        pt = { x: q.x, y: q.y }
      } else {
        pt = {
          x: this.mouth.x + (this.nozzle.x - this.mouth.x) * sm,
          y: this.mouth.y + (this.nozzle.y - this.mouth.y) * sm,
        }
      }
      sx = 0.88
      sy = 1.1
    } else {
      const target = this.sock[flight.pick - 1] ?? this.nozzle
      const span = Math.max(0, this.nozzle.y - target.y)
      const cTopA = { x: this.gutX + 2 * u, y: this.nozzle.y - span * 0.58 }
      const cTopB = {
        x: this.gutX + 2 * u,
        y: Math.min(this.nozzle.y - 6 * u, target.y + Math.max(16 * u, span * 0.34)),
      }
      const ec = 1 - Math.pow(1 - t, 2.35)
      pt = bez3(this.nozzle, cTopA, cTopB, target, ec)
      const k2 = (1 - t) * (1 - t)
      sx = 1 - 0.16 * k2
      sy = 1 + 0.2 * k2
      fillY = Math.min(fillY, pt.y, this.nozzle.y)
    }

    this.funnel.classList.toggle('is-open', flight.segment === 'funnel')
    // The lamp fires as the ball clears the nozzle; the mockup held it for
    // 170ms of a 190-430ms climb, which is the first ~0.4 of the segment.
    this.lamp?.classList.toggle('is-hot', flight.segment === 'gutter' && t < 0.4)

    // classList.toggle only mutates when the state actually changes, so the
    // pulse starts once on entering the tube and runs its own 460ms out rather
    // than restarting every frame.
    this.flow?.classList.toggle('is-on', flight.segment === 'tube')

    this.fillGutterTo(fillY)

    this.setFlyLabel(flight.label)
    this.flyer?.setAttribute('opacity', '1')
    this.flyer?.setAttribute(
      'transform',
      `translate(${pt.x.toFixed(2)},${pt.y.toFixed(2)}) scale(${sx.toFixed(3)},${sy.toFixed(3)})`,
    )

    this.trail.unshift(pt)
    if (this.trail.length > 10) this.trail.pop()
    this.ghosts.forEach((g, i) => {
      const hp = this.trail[(i + 1) * 3]
      if (!hp) {
        g.setAttribute('opacity', '0')
        return
      }
      g.setAttribute('cx', String(round(hp.x)))
      g.setAttribute('cy', String(round(hp.y)))
      g.setAttribute('opacity', String(round(0.34 - i * 0.1)))
    })
  }

  destroy(): void {
    this.trail = []
    this.trailPick = null
    this.measured = false
  }

  /* The same balanced break the canvas sprite uses, sized off the ball's own
   * radius. The canvas can measure its text; SVG cannot without a reflow per
   * frame, so this estimates from the character count — the flyer is in the air
   * for a few hundred milliseconds and a few percent of slack is invisible. */
  private setFlyLabel(label: string): void {
    if (!this.flyTxt) return
    if (label === this.flyLabel) return
    this.flyLabel = label

    const r = FLY.body * this.u
    const { lines, chars } = fitBallText(label)
    const size = Math.min((r * 1.5) / (chars * CHAR_W), (r * 1.45) / (lines.length * LINE_H))

    this.flyTxt.setAttribute('font-size', String(round(size)))
    this.flyTxt.textContent = ''
    const step = size * LINE_H
    const top = -((lines.length - 1) * step) / 2 + size * 0.34
    for (const [ i, line ] of lines.entries()) {
      const span = document.createElementNS('http://www.w3.org/2000/svg', 'tspan')
      span.setAttribute('x', '0')
      span.setAttribute('y', String(round(top + i * step)))
      span.textContent = line
      this.flyTxt.appendChild(span)
    }
  }

  private setStroke(sel: string, w: number): void {
    this.svg.querySelector(sel)?.setAttribute('stroke-width', String(round(w)))
  }

  private fillGutterTo(y: number): void {
    if (!this.gutFill) return
    const base = this.nozzle.y + GUT_BASE_DROP * this.u
    this.gutFill.setAttribute('y', String(round(y)))
    this.gutFill.setAttribute('height', String(round(Math.max(0, base - y))))
  }
}
